'use client'

import {
  createElement,
  JSXElementConstructor,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { IntlProvider } from 'react-intl'

import { AuthModalProvider } from '@marketplace-web/domain/identity'
import { UserStatsProvider } from '@marketplace-web/domain/user-stats-data'
import { AbTestsProvider } from '@marketplace-web/shared/ab-tests'
import { BreakpointProvider } from '@marketplace-web/shared/breakpoints'
import { CookieManagerProvider, createCookieManager } from '@marketplace-web/shared/cookies'
import { FeatureSwitchesProvider } from '@marketplace-web/shared/feature-switches'
import { RequestProvider } from '@marketplace-web/shared/request'
import { SessionProvider } from '@marketplace-web/shared/session'
import { SystemConfigurationProvider } from '@marketplace-web/shared/system-configuration'

import { BrazeProvider } from '@marketplace-web/shared/braze'
import { TrackingContext } from '@marketplace-web/shared/event-tracker'

type ProviderProps<T extends JSXElementConstructor<any>> = Omit<ComponentProps<T>, 'children'>

type Props = {
  featureSwitchesProps: ProviderProps<typeof FeatureSwitchesProvider>
  systemConfigurationProps: ProviderProps<typeof SystemConfigurationProvider>
  sessionProps: ProviderProps<typeof SessionProvider>
  abTestsProps: ProviderProps<typeof AbTestsProvider>
  intlProps: ProviderProps<typeof IntlProvider>
  cookies: Record<string, string | undefined>
  nextRequestProps: ProviderProps<typeof RequestProvider>
  breakpointProps: ProviderProps<typeof BreakpointProvider>
}

const createProvider =
  <T extends JSXElementConstructor<any>>(provider: T, props?: ProviderProps<T>) =>
  (children: ReactNode) =>
    createElement(provider, props, children)

const ClientDataContextProviders = ({
  children,
  featureSwitchesProps,
  systemConfigurationProps,
  sessionProps,
  abTestsProps,
  intlProps,
  cookies,
  nextRequestProps,
  breakpointProps,
}: PropsWithChildren<Props>) => {
  const cookieManagerRef = useRef<ReturnType<typeof createCookieManager> | null>(null)
  cookieManagerRef.current ??= createCookieManager(cookies)
  const trackingContext = useContext(TrackingContext)

  const { user, debugPin, anonId, trackingPlatform, screen } = sessionProps.initialSessionData
  const { portal } = systemConfigurationProps.configuration

  useEffect(() => {
    if (!trackingContext?.tracker) return

    trackingContext.tracker.updateContext({
      anonId: anonId || '',
      userId: user?.id,
      portal: portal || '',
      debugPin: debugPin ? Number(debugPin) : null,
      platform: trackingPlatform || null,
      languageCode: user?.locale || '',
      screen: {
        width: 0,
        height: 0,
        name: screen,
      },
      utm: {},
    })

    trackingContext.tracker.drainEventQueue()
  }, [trackingContext, anonId, portal, debugPin, user?.id, user?.locale, trackingPlatform, screen])

  return [
    createProvider(FeatureSwitchesProvider, featureSwitchesProps),
    createProvider(SystemConfigurationProvider, systemConfigurationProps),
    createProvider(SessionProvider, sessionProps),
    createProvider(AbTestsProvider, abTestsProps),
    createProvider(IntlProvider, intlProps),
    createProvider(UserStatsProvider, { userId: sessionProps.initialSessionData.user?.id }),
    createProvider(CookieManagerProvider, { cookieManager: cookieManagerRef.current }),
    createProvider(RequestProvider, nextRequestProps),
    createProvider(BrazeProvider),
    createProvider(BreakpointProvider, breakpointProps),
    createProvider(AuthModalProvider),
  ].reduceRight((providerChildren, provider) => provider(providerChildren), children)
}

export default ClientDataContextProviders
